import { Box } from '@mui/material';

import {
    AssistantLoadingSkeleton,
    AssistantMessageBubble,
    ErrorMessageBubble,
    UserMessageBubble,
} from './MessageBubbles';
import { CopyGeneratorChatState } from './utils';

const GREETING_MESSAGE =
    "Hi! I'm Sara, your AI writing assistant. Tell me what you'd like to write and I'll help you craft it.";

export type Props = {
    chatState: CopyGeneratorChatState;
    errorMessage?: string | null;
    isLoading?: boolean;
    onRetry?: () => void;
};

/**
 * Scrollable transcript for Ask Sara. Always renders a greeting as the first message.
 * Assistant: left-aligned + sparkle, no fill. User: right-aligned mint bubble.
 */
const MessagesDisplay = ({ chatState, errorMessage, isLoading = false, onRetry }: Props) => {
    const { messages } = chatState;

    const lastAssistantIndex = messages.reduce<number>((acc, msg, i) => (msg.role === 'assistant' ? i : acc), -1);

    return (
        <Box
            aria-label="Chat messages"
            data-testid="copy-generator-chat-messages-placeholder"
            role="region"
            sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: 2,
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
            }}
        >
            <Box data-testid="copy-generator-message-greeting" aria-label="Assistant message" role="article">
                <AssistantMessageBubble content={GREETING_MESSAGE} />
            </Box>

            {messages.map((message, index) => {
                const key = `${message.role}-${index}`;
                if (message.role === 'assistant') {
                    const isLastAssistant = index === lastAssistantIndex;
                    return (
                        <Box
                            key={key}
                            data-testid={`copy-generator-message-assistant-${index}`}
                            aria-label="Assistant message"
                            role="article"
                        >
                            <AssistantMessageBubble
                                content={message.content}
                                isLoading={isLoading}
                                onRetry={isLastAssistant ? onRetry : undefined}
                            />
                        </Box>
                    );
                }

                return (
                    <Box
                        key={key}
                        data-testid={`copy-generator-message-user-${index}`}
                        aria-label="Your message"
                        role="article"
                    >
                        <UserMessageBubble content={message.content} />
                    </Box>
                );
            })}

            {isLoading && <AssistantLoadingSkeleton />}

            {errorMessage && (
                <Box data-testid="copy-generator-message-error" aria-label="Error message" role="alert">
                    <ErrorMessageBubble content={errorMessage} />
                </Box>
            )}
        </Box>
    );
};

export default MessagesDisplay;
